import React, { useEffect, useState } from "react";
import {
  Avatar, Box, Chip, Stack, Table, TableBody, TableCell, TableHead,
  TablePagination, TableRow, Typography,
} from "@mui/material";
import HotelIcon from "@mui/icons-material/Hotel";
import { NURSE_COLORS } from "../../nurse-dashboard/theme";
import { statusCfg } from "../utils/bedStatus";
import { formatIstDateTime } from "../../../utils/istDateTime";

// Pinned to IST rather than left to the browser's zone. The backend stores and
// serves UTC (settings.USE_TZ), so a workstation whose clock is not on IST
// rendered the admission time hours out. The " IST" suffix is a literal — under
// en-GB, timeZoneName: "short" renders "GMT+5:30", not "IST".
//
// Pinning the output zone alone was not enough: main_ipd_admission.admitted_at
// is a `timestamp without time zone` column, so the UTC instant arrives with no
// "Z" and new Date() read it as local — every admission still showed 5h30m
// early. formatIstDateTime restores the marker before converting.
function fmtAdmittedAt(ts) {
  const at = formatIstDateTime(ts);
  return at ? `${at} IST` : "—";
}

// Shared bed census table (ADR 0001 epic) — consumed by both BedAllocation.js
// and the nurse dashboard's BedDashboard.js. Core columns (Bed, Ward/Room,
// Type, Patient, Consultant, Payment Details, Admitted, Status) are fixed;
// each caller supplies its own action set and may append extra columns
// (e.g. nurse dashboard's "Assigned Nurses").
export default function BedCensusTable({
  beds,
  admissionByBed,
  extraColumns = [],
  renderActions,
  onRowClick,
  emptyMessage = "No beds match the current filters.",
  loading = false,
  paginate = false,
  rowsPerPageOptions = [10, 25, 50],
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  // Snap back to a valid page if filtering/reloading shrinks the list out
  // from under the currently-viewed page — but don't reset on every reload.
  useEffect(() => {
    if (paginate && page > 0 && page * rowsPerPage >= beds.length) {
      setPage(0);
    }
  }, [paginate, beds.length, page, rowsPerPage]);

  const pageBeds = paginate ? beds.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : beds;

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
            <TableCell sx={{ fontWeight: 600 }}>Bed</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Ward / Room</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Consultant</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Payment Details</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Admission Source</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Admitted</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            {extraColumns.map((col) => (
              <TableCell key={col.header} sx={{ fontWeight: 600 }}>{col.header}</TableCell>
            ))}
            {renderActions && <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {pageBeds.map((bed) => {
            const cfg = statusCfg(bed.status);
            const admission = admissionByBed[bed.code];
            const clickable = Boolean(onRowClick) && cfg.clickable;
            return (
              <TableRow key={bed.id ?? bed.code} hover
                onClick={() => clickable && onRowClick(bed, admission)}
                sx={{ cursor: clickable ? "pointer" : "default",
                      "&:hover td": clickable ? { bgcolor: NURSE_COLORS.accentSoft } : {} }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{bed.code}</Typography>
                  {bed.name && bed.name !== bed.code && (
                    <Typography variant="caption" color="text.secondary">{bed.name}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {/* Ward > Room, where the ward is room_category (ICU / General /
                      ITU / Private). bed.ward_name is NOT read: it carries a
                      department name — see useBedCensusData. */}
                  <Typography variant="body2">
                    {bed.room_category_name || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bed.room_name || bed.location || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{bed.bed_type_code || "—"}</Typography>
                </TableCell>
                <TableCell>
                  {admission
                    ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ bgcolor: NURSE_COLORS.primary, width: 26, height: 26, fontSize: 11 }}>
                          {admission.patientName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{admission.patientName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[admission.uhid, admission.age != null ? `${admission.age}${admission.gender}` : null]
                              .filter(Boolean).join(" · ")}
                          </Typography>
                        </Box>
                      </Stack>
                    )
                    : <Typography variant="body2" color="text.secondary">—</Typography>
                  }
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{admission?.consultantName || "—"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{admission?.payerType || "—"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{admission?.admissionSource || "—"}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{admission ? fmtAdmittedAt(admission.admittedAt) : "—"}</Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={cfg.label} color={cfg.chipColor}
                    variant={String(bed.status).toLowerCase() === "available" ? "outlined" : "filled"} />
                </TableCell>
                {extraColumns.map((col) => (
                  <TableCell key={col.header}>{col.render(bed, admission)}</TableCell>
                ))}
                {renderActions && (
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {renderActions(bed, admission)}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          {!loading && beds.length === 0 && (
            <TableRow>
              <TableCell colSpan={9 + extraColumns.length + (renderActions ? 1 : 0)} align="center">
                <Box sx={{ py: 4 }}>
                  <HotelIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {paginate && beds.length > 0 && (
        <TablePagination
          component="div"
          count={beds.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </Box>
  );
}
